import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Star, CheckCircle2, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { API } from '@/utils/api';
import axios from 'axios';

const SatisfactionPage = () => {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rating, setRating] = useState(0);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    axios.get(`${API}/client-notifications/satisfaction/${token}`)
      .then(res => { setInfo(res.data); if (res.data.already_rated) setSubmitted(true); })
      .catch(() => setError('Ce lien est invalide ou a expire.'))
      .finally(() => setLoading(false));
  }, [token]);

  const handleSubmit = async () => {
    if (rating === 0) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/client-notifications/satisfaction/${token}`, { rating, message });
      setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de l\'envoi');
    } finally {
      setSubmitting(false);
    }
  };

  const ratingLabels = ['', 'Pas satisfait', 'Peu satisfait', 'Satisfait', 'Tres satisfait', 'Entierement satisfait'];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error && !info) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <div className="text-center max-w-sm">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <p className="text-slate-600">{error}</p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <div className="text-center max-w-sm" data-testid="satisfaction-thank-you">
          <CheckCircle2 className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-slate-800 mb-2">Merci pour votre avis !</h1>
          <p className="text-slate-500 text-sm">Votre retour nous aide a nous ameliorer.</p>
          <p className="text-slate-400 text-xs mt-4">{info?.garage_name || 'Vortekio'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
      <div className="w-full max-w-md" data-testid="satisfaction-form">
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 p-6 text-center">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center mx-auto">
              <span className="text-white font-bold text-lg">V</span>
            </div>
            <p className="text-slate-400 text-xs mt-2 tracking-wider">VORTEKIO</p>
            <h1 className="text-white font-semibold text-base mt-3">Comment s'est passe votre visite ?</h1>
            {info?.vehicle && <p className="text-blue-300 text-sm mt-1">{info.vehicle}</p>}
          </div>

          <div className="p-6 space-y-5">
            <p className="text-slate-600 text-sm text-center">
              Bonjour <strong>{info?.client_name}</strong>, donnez-nous votre avis :
            </p>

            {/* Stars */}
            <div className="flex justify-center gap-2" data-testid="star-rating">
              {[1, 2, 3, 4, 5].map(star => (
                <button
                  key={star}
                  onClick={() => setRating(star)}
                  onMouseEnter={() => setHoveredRating(star)}
                  onMouseLeave={() => setHoveredRating(0)}
                  className="p-1 transition-transform hover:scale-110"
                  data-testid={`star-${star}`}
                >
                  <Star
                    className={`w-10 h-10 transition-colors ${
                      star <= (hoveredRating || rating)
                        ? 'text-amber-400 fill-amber-400'
                        : 'text-slate-200'
                    }`}
                  />
                </button>
              ))}
            </div>

            {/* Rating label */}
            {(hoveredRating || rating) > 0 && (
              <p className="text-center text-sm font-medium text-slate-700" data-testid="rating-label">
                {ratingLabels[hoveredRating || rating]}
              </p>
            )}

            {/* Message */}
            <div>
              <label className="text-sm text-slate-500 mb-1 block">Un commentaire ? (optionnel)</label>
              <Textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Partagez votre experience..."
                rows={3}
                className="resize-none"
                data-testid="satisfaction-message"
              />
            </div>

            {error && <p className="text-red-500 text-sm text-center">{error}</p>}

            <Button
              onClick={handleSubmit}
              disabled={rating === 0 || submitting}
              className="w-full h-12 rounded-xl font-semibold bg-blue-600 hover:bg-blue-700"
              data-testid="submit-satisfaction"
            >
              {submitting ? 'Envoi...' : 'Envoyer mon avis'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SatisfactionPage;
